import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TownData {
  id: string;
  name: string;
  slug: string;
  state: string | null;
  intro_summary: string | null;
  hero_image_url: string | null;
}

interface AreaData {
  id: string;
  name: string;
  slug: string;
  intro_summary: string | null;
  town_id: string;
}

interface RealtorData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url: string | null;
  title_preference: 'agent' | 'realtor' | null;
  ad_image_url: string | null;
}

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string;
  category: string;
  main_image_url: string | null;
}

// Cache times: 5 min stale, 30 min in cache
const STALE_TIME = 5 * 60 * 1000;
const GC_TIME = 30 * 60 * 1000;

interface GalleryImage {
  url: string;
  attribution?: string;
  type?: 'image' | 'video';
  videoId?: string;
}

interface TownGalleryRow {
  id: string;
  name: string;
  hero_image_url: string | null;
  gallery_images: GalleryImage[] | null;
}

export function useTowns(siteId: string) {
  return useQuery({
    queryKey: ['towns', siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('towns')
        .select('id, name, slug, state, intro_summary, hero_image_url')
        .eq('site_id', siteId)
        .eq('is_published', true)
        .order('name');

      if (error) throw error;
      return (data || []) as TownData[];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!siteId,
  });
}

export function useTownGalleryImages(siteId: string) {
  return useQuery({
    queryKey: ['townGalleryImages', siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('towns')
        .select('id, name, hero_image_url, gallery_images')
        .eq('site_id', siteId)
        .eq('is_published', true)
        .order('name');

      if (error) throw error;
      
      // Collect all images from all towns (gallery first, then hero fallback)
      const allImages: { url: string; attribution?: string; townName: string; type?: 'image' | 'video'; videoId?: string }[] = [];
      
      for (const town of (data || []) as unknown as TownGalleryRow[]) {
        // Collect gallery image URLs for this town to check hero duplication
        const galleryUrls = new Set<string>();
        
        // Add gallery images first (they have proper type/videoId metadata)
        if (town.gallery_images && Array.isArray(town.gallery_images)) {
          for (const img of town.gallery_images) {
            if (img.url) {
              galleryUrls.add(img.url);
              allImages.push({ 
                url: img.url, 
                attribution: img.attribution,
                townName: town.name,
                type: img.type || 'image',
                videoId: img.videoId
              });
            }
          }
        }
        
        // Add hero image only if it's not already in gallery (avoid duplicate without video metadata)
        if (town.hero_image_url && !galleryUrls.has(town.hero_image_url)) {
          allImages.push({ url: town.hero_image_url, townName: town.name, type: 'image' });
        }
      }
      
      // Deduplicate by URL
      const uniqueImages = allImages.filter((img, index, self) => 
        index === self.findIndex(i => i.url === img.url)
      );
      
      return uniqueImages;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!siteId,
  });
}

export function useAreas(siteId: string) {
  return useQuery({
    queryKey: ['areas', siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('areas')
        .select('id, name, slug, intro_summary, town_id')
        .eq('site_id', siteId)
        .eq('is_published', true)
        .order('name');

      if (error) throw error;
      return (data || []) as AreaData[];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!siteId,
  });
}

export function useRealtor(siteId: string) {
  return useQuery({
    queryKey: ['realtor', siteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('realtor_profiles')
        .select('id, first_name, last_name, photo_url, phone, city, state, license_numbers, about_me, logo_url, title_preference, ad_image_url')
        .eq('site_id', siteId)
        .maybeSingle();

      if (error) throw error;
      return data as RealtorData | null;
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!siteId,
  });
}

export function useBlogPosts(siteId: string, limit = 5) {
  return useQuery({
    queryKey: ['blogPosts', siteId, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, published_at, category, main_image_url')
        .eq('site_id', siteId)
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []) as BlogPostData[];
    },
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!siteId,
  });
}

// Combine towns + areas for display
export function useTownsWithAreas(siteId: string) {
  const { data: towns, isLoading: townsLoading } = useTowns(siteId);
  const { data: areas, isLoading: areasLoading } = useAreas(siteId);

  const townsWithAreas = (towns || []).map((town) => ({
    ...town,
    areas: (areas || []).filter((a) => a.town_id === town.id),
  }));

  return {
    towns: townsWithAreas,
    areas: areas || [], // Flat list for footer
    isLoading: townsLoading,
    areasLoading,
  };
}

// Export types for reuse
export type { TownData, AreaData, RealtorData, BlogPostData };
